import { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';

/**
 * Hook pour récupérer et vérifier les permissions d'un commerçant
 * @param {string} commercantId - ID du commerçant
 * @returns {object} { permissions, hasPermission, loading, refetch }
 */
export const usePermissions = (commercantId) => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Récupérer le rôle de l'utilisateur actuel
  const getCurrentUserRole = () => {
    try {
      const userData = localStorage.getItem('admin_user');
      if (userData) {
        const user = JSON.parse(userData);
        return user?.role || null;
      }
    } catch (error) {
      console.error('Erreur parsing user data:', error);
    }
    return null;
  };

  const fetchPermissions = useCallback(async () => {
    if (!commercantId) {
      setPermissions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const perms = await apiService.getCommercantPermissions(commercantId);
      // Si l'API retourne un tableau de permissions directement
      const permissionsList = Array.isArray(perms) 
        ? perms 
        : (Array.isArray(perms?.permissions) ? perms.permissions : []);
      
      // Extraire les noms de permissions si ce sont des objets avec une propriété 'permission'
      const permissionNames = permissionsList.map(p => 
        typeof p === 'string' ? p : (p?.permission || p)
      );
      
      setPermissions(permissionNames);
    } catch (err) {
      console.error('Erreur chargement permissions:', err);
      setError(err);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, [commercantId]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  /**
   * Vérifie si le commerçant a une permission spécifique
   * @param {string} permission - Permission à vérifier (ex: 'GESTION_PRODUITS')
   * @returns {boolean}
   */
  const hasPermission = useCallback((permission) => {
    // SUPER_ADMIN a toutes les permissions
    const userRole = getCurrentUserRole();
    if (userRole === 'SUPER_ADMIN' || userRole === 'ADMIN') {
      return true;
    }

    // Vérifier si la permission est dans la liste
    return permissions.includes(permission);
  }, [permissions]);

  return {
    permissions,
    hasPermission,
    loading,
    error,
    refetch: fetchPermissions
  };
};

export default usePermissions;







